import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import '../Styling/Amazon_BC_inv_header_errorScreen.css';
import { Base_URL } from '../url';

const ITEMS_PER_PAGE = 50;

const Amazon_BC_inv_header_errorScreen = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('asc');

  /* ---------- Filters ---------- */
  const [selectedPostingDate, setSelectedPostingDate] = useState('');
  const [selectedErrorId, setSelectedErrorId] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const API_URL = `${Base_URL}/Amazon_Screen_Request/GetAmazonBcInvHeaderErrors`;

  /* ---------- Initial API Call ---------- */
  useEffect(() => {
    fetchErrorData();
  }, []);

  const TableLoader = () => (
    <tr>
      <td colSpan="10" className="amazon-bc-inv-header-error-screen-loader">
        <div className="amazon-bc-inv-header-error-screen-loader-card">
          <div className="amazon-bc-inv-header-error-screen-loader-icon">📅</div>
          <div className="amazon-bc-inv-header-error-screen-loader-text">Loading orders…</div>
        </div>
      </td>
    </tr>
  );

  const fetchErrorData = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(API_URL, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000,
      });

      console.log(res.data);

      const apiData = Array.isArray(res.data?.data) ? res.data.data : [];
      setData(apiData);
      setFilteredData(apiData);
      setCurrentPage(1);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to load header errors'
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Helper: Parse Error Description ---------- */
  const parseErrorDesc = (errorDesc) => {
    if (!errorDesc) return '-';
    
    try {
      // Try to parse as JSON
      const parsed = JSON.parse(errorDesc);
      
      // Check if it has nested error object
      if (parsed.error && parsed.error.code && parsed.error.message) {
        return {
          code: parsed.error.code,
          message: parsed.error.message
        };
      }
      
      // Check if it has direct code and message
      if (parsed.code && parsed.message) {
        return {
          code: parsed.code,
          message: parsed.message
        };
      }
      
      // If JSON but no recognized structure, return original
      return errorDesc;
    } catch (e) {
      // Not JSON, return as-is
      return errorDesc;
    }
  };

  /* ---------- Excel Download Handler ---------- */
  const handleDownloadExcel = () => {
    if (filteredData.length === 0) return;

    setDownloading(true);

    try {
      // Prepare data for Excel with proper formatting
      const excelData = filteredData.map((row) => {
        const parsed = parseErrorDesc(row.errorDesc);
        
        // Extract error code and message based on parsed result
        let errorCode = '';
        let errorMessage = '';
        
        if (typeof parsed === 'object' && parsed.code && parsed.message) {
          errorCode = parsed.code;
          errorMessage = parsed.message;
        } else if (typeof parsed === 'string' && parsed !== '-') {
          // Plain text error goes to message, code stays blank
          errorMessage = parsed;
        }

        return {
          'Order Date': row.orderDate ? row.orderDate : '',
          'Posting Date': row.postingDate ? row.postingDate : '',
          'External Document Number': row.externalDocumentNumber || '',
          'Warehouse ID': row.warehouseid || '',
          'Error ID': row.errorId || '',
          'Error Code': errorCode,
          'Error Message': errorMessage,
          'Customer ID': row.customerId || '',
        };
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      worksheet['!cols'] = [
        { wch: 12 },  // Order Date
        { wch: 12 },  // Posting Date
        { wch: 22 },  // External Document Number
        { wch: 15 },  // Warehouse ID
        { wch: 10 },  // Error ID
        { wch: 15 },  // Error Code
        { wch: 50 },  // Error Message (widest)
        { wch: 15 },  // Customer ID
      ];

      // Apply date formatting to Order Date and Posting Date columns
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        // Order Date column (A)
        const orderDateCell = XLSX.utils.encode_cell({ r: R, c: 0 });
        if (worksheet[orderDateCell] && worksheet[orderDateCell].v) {
          worksheet[orderDateCell].t = 'd';
          worksheet[orderDateCell].z = 'dd/mm/yyyy';
        }
        
        // Posting Date column (B)
        const postingDateCell = XLSX.utils.encode_cell({ r: R, c: 1 });
        if (worksheet[postingDateCell] && worksheet[postingDateCell].v) {
          worksheet[postingDateCell].t = 'd';
          worksheet[postingDateCell].z = 'dd/mm/yyyy';
        }
      }

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Header Errors');

      // Generate filename with current date
      const today = new Date().toISOString().split('T')[0];
      const filename = `Amazon_BC_Header_Errors_${today}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      // Show success feedback
      setTimeout(() => {
        setDownloading(false);
      }, 500);

    } catch (err) {
      console.error('Error generating Excel file:', err);
      alert('Failed to generate Excel file. Please try again.');
      setDownloading(false);
    }
  };

  /* ---------- Apply Filters ---------- */
  useEffect(() => {
    setFiltering(true);

    const timer = setTimeout(() => {
      let temp = [...data];

      if (selectedPostingDate) {
  temp = temp.filter(
    (x) =>
      x.postingDate &&
      x.postingDate.split("T")[0] === selectedPostingDate
  );
}



      if (selectedErrorId) {
        temp = temp.filter((x) => String(x.errorId) === selectedErrorId);
      }

      if (selectedCustomerId) {
        temp = temp.filter((x) => x.customerId === selectedCustomerId);
      }

      if (selectedWarehouseId) {
        temp = temp.filter((x) => x.warehouseid === selectedWarehouseId);
      }

      setFilteredData(temp);
      setCurrentPage(1);
      setFiltering(false);
    }, 400); // smooth loader feel

    return () => clearTimeout(timer);
  }, [
    selectedPostingDate,
    selectedErrorId,
    selectedCustomerId,
    selectedWarehouseId,
    data,
  ]);

  /* ---------- Sorting ---------- */
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = a.externalDocumentNumber || '';
      const bVal = b.externalDocumentNumber || '';
      return sortOrder === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredData, sortOrder]);

  const clearFilters = () => {
    setFiltering(true);

    setTimeout(() => {
      setSelectedPostingDate('');
      setSelectedErrorId('');
      setSelectedCustomerId('');
      setSelectedWarehouseId('');
      setFilteredData(data);
      setCurrentPage(1);
      setFiltering(false);
    }, 300);
  };

  /* ---------- Pagination ---------- */
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedData.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedData, currentPage]);

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString('en-GB') : '-';

  /* ---------- Filter Options ---------- */
  const unique = (key) => [...new Set(data.map((x) => x[key]).filter(Boolean))];

  const uniquePostingDates = useMemo(() => {
  const normalized = data
    .map((x) => x.postingDate?.split("T")[0])
    .filter(Boolean);

  return [...new Set(normalized)].sort();
}, [data]);



  /* ---------- Main Render ---------- */
  return (
    <div className="amazon-bc-inv-header-error-screen">
      <div className="error-report-container">
        <div className="table-header">
          <div className="header-left">
            <h2>Amazon BC Invoice Header Errors</h2>
            <span className="record-count">
              Showing {filteredData.length} records
            </span>
          </div>
          <button 
            className="download-excel-button" 
            onClick={handleDownloadExcel}
            disabled={filteredData.length === 0 || downloading}
          >
            {downloading ? '⏳ Downloading...' : '📥 Download Excel'}
          </button>
        </div>

        {/* ---------- Filters ---------- */}
        <div className="filter-bar">
          <select
  value={selectedPostingDate}
  onChange={(e) => setSelectedPostingDate(e.target.value)}
>
  <option value="">Posting Date</option>

  {uniquePostingDates.map((date) => (
    <option key={date} value={date}>
      {new Date(date).toLocaleDateString('en-GB')}
    </option>
  ))}
</select>


          <select
            value={selectedErrorId}
            onChange={(e) => setSelectedErrorId(e.target.value)}
          >
            <option value="">Error ID</option>
            {unique('errorId').map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>

          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
          >
            <option value="">Customer ID</option>
            {unique('customerId').map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>

          <select
            value={selectedWarehouseId}
            onChange={(e) => setSelectedWarehouseId(e.target.value)}
          >
            <option value="">Warehouse</option>
            {unique('warehouseid').map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>

          <button
            className="clear-filter-button"
            onClick={clearFilters}
            disabled={
              !selectedPostingDate &&
              !selectedErrorId &&
              !selectedCustomerId &&
              !selectedWarehouseId
            }
          >
            Clear Filters
          </button>
        </div>

        {/* ---------- Table ---------- */}
        <div className="table-wrapper">
          <table className="error-report-table">
            <thead>
              <tr>
                <th>Posting Date</th>
                <th>Order Date</th>
                <th
                  className="sortable"
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                >
                  External Doc #
                </th>
                <th>Warehouse</th>
                <th>Error ID</th>
                <th>Error Description</th>
                <th>Customer ID</th>
              </tr>
            </thead>

            <tbody>
              {/* Show error state */}
              {error && !loading && !filtering ? (
                <tr>
                  <td colSpan="7" className="amazon-bc-inv-header-error-screen-error-cell">
                    <div className="amazon-bc-inv-header-error-screen-error-card">
                      <div className="amazon-bc-inv-header-error-screen-error-icon">⚠️</div>
                      <div className="amazon-bc-inv-header-error-screen-error-content">
                        <h3>Error Loading Data</h3>
                        <p>{error}</p>
                      </div>
                      <button className="retry-button" onClick={fetchErrorData}>
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (loading || filtering) ? (
                /* Show loader ONLY when loading or filtering */
                <TableLoader />
              ) : paginatedData.length === 0 ? (
                /* Show empty state when no data */
                <tr>
                  <td colSpan="7" className="amazon-bc-inv-header-error-screen-empty-cell">
                    <div className="amazon-bc-inv-header-error-screen-empty-card">
                      <div className="amazon-bc-inv-header-error-screen-empty-icon">📭</div>
                      <div className="amazon-bc-inv-header-error-screen-empty-content">
                        <h3>No Records Found</h3>
                        <p>Try adjusting your filters or refresh the data</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                /* Show actual data rows ONLY when not loading/filtering */
                paginatedData.map((row, index) => (
                  <tr key={index}>
                    <td>{formatDate(row.postingDate)}</td>
                    <td>{formatDate(row.orderDate)}</td>
                    <td className="document-number">
                      {row.externalDocumentNumber}
                    </td>
                    <td>{row.warehouseid || '-'}</td>
                    <td>{row.errorId}</td>
                    <td className="error-desc">
                      {(() => {
                        const parsed = parseErrorDesc(row.errorDesc);
                        
                        if (typeof parsed === 'object' && parsed.code && parsed.message) {
                          return (
                            <div className="error-desc-content">
                              <span className="error-desc-code">{parsed.code}</span>
                              <span className="error-desc-message">{parsed.message}</span>
                            </div>
                          );
                        }
                        
                        return parsed;
                      })()}
                    </td>
                    <td>{row.customerId}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ---------- Pagination ---------- */}
        <div className="pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Amazon_BC_inv_header_errorScreen;